import { useBreadcrumbs } from '@context/BreadcrumbContext'
import {
	useCreateItineraryImport,
	useEvents,
	useItineraryImportStatus,
	useRetryItineraryImport,
	useTrip,
} from '@hooks'
import {
	ApiErrorResponse,
	getApiErrorResponse,
	getErrorMessage,
	isBadRequest,
	isPayloadTooLarge,
	isRateLimited,
	isServerError,
} from '@lib/apiError'
import { queryKeys } from '@lib/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { STUCK_THRESHOLD_MS } from './constants'
import { PageState } from './types'
import { countPdfPages, FileEntry, validateFile } from './utils'

const newFileId = (): string =>
	globalThis.crypto && 'randomUUID' in globalThis.crypto
		? globalThis.crypto.randomUUID()
		: `f_${Math.random().toString(36).slice(2)}_${Date.now()}`

interface ServerValidationErrorData {
	message?: string
	errors?: {
		formErrors?: string[]
		fieldErrors?: Record<string, string[] | undefined>
	}
}

const getServerValidationMessage = (response: ApiErrorResponse): string => {
	const data = response.data as ServerValidationErrorData | undefined
	if (typeof data?.message === 'string') return data.message
	if (data?.errors?.formErrors?.length) return data.errors.formErrors.join(' ')
	if (data?.errors?.fieldErrors) {
		const fieldMessages = Object.values(data.errors.fieldErrors).flat()
		if (fieldMessages.length) return fieldMessages.join(' ')
	}
	return response.status === 413
		? 'Total upload size exceeds the request limit'
		: 'A few files need attention'
}

/**
 * Owns all state, data-fetching, and handlers for the itinerary import page.
 * The page component just renders the section that matches `pageState`.
 */
export const useImportController = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const importIdFromUrl = searchParams.get('job') ?? undefined

	const { data: trip, isLoading: tripLoading } = useTrip(tripId)

	const [files, setFiles] = useState<FileEntry[]>([])
	const [notes, setNotes] = useState('')
	const [isDragOver, setIsDragOver] = useState(false)
	const [serverValidationMessage, setServerValidationMessage] = useState<string | undefined>()
	const [importId, setImportId] = useState<string | undefined>(importIdFromUrl)
	const [overrideState, setOverrideState] = useState<
		{ kind: 'rate_limit'; retryAfter: number } | { kind: 'failed'; reference?: string } | undefined
	>(undefined)
	const [processingStartedAt, setProcessingStartedAt] = useState<number | null>(null)

	const fileInputRef = useRef<HTMLInputElement | null>(null)
	const dragCounter = useRef(0)
	const invalidatedImportRef = useRef<string | undefined>(undefined)

	const queryClient = useQueryClient()
	const createImport = useCreateItineraryImport()
	const retryImport = useRetryItineraryImport()
	const importStatus = useItineraryImportStatus({ tripId, importId, enabled: !!importId })

	const { data: eventsData } = useEvents(importStatus.data?.status === 'Success' ? tripId : undefined)

	useBreadcrumbs(
		trip
			? [
					{ label: 'My Trips', path: '/trips' },
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Itinerary', path: `/trips/${tripId}/itinerary` },
					{ label: 'Import' },
				]
			: [],
	)

	useEffect(() => {
		if (!processingStartedAt && importStatus.data?.status === 'Processing') {
			setProcessingStartedAt(Date.now())
		}
	}, [importStatus.data?.status, processingStartedAt])

	useEffect(() => {
		if (!tripId || !importId) return
		if (importStatus.data?.status !== 'Success') return
		if (invalidatedImportRef.current === importId) return
		invalidatedImportRef.current = importId
		for (const key of queryKeys.itineraryContentKeys(tripId)) {
			queryClient.invalidateQueries({ queryKey: key })
		}
	}, [importStatus.data?.status, importId, tripId, queryClient])

	useEffect(() => {
		if (importId && !importIdFromUrl) {
			setSearchParams({ job: importId }, { replace: true })
		}
	}, [importId, importIdFromUrl, setSearchParams])

	const fileValidationErrorCount = useMemo(() => files.reduce((n, f) => (f.error ? n + 1 : n), 0), [files])

	const validationErrorCount = fileValidationErrorCount + (serverValidationMessage ? 1 : 0)
	const hasValidationErrors = validationErrorCount > 0
	const submitDisabled =
		(notes.trim().length === 0 && files.length === 0) || hasValidationErrors || createImport.isPending

	// Anchor the fake-progress timer to the import's server-side createdAt so it
	// survives a page refresh; fall back to the locally-recorded start until the
	// first status poll returns.
	const processingStartedAtMs = importStatus.data?.createdAt
		? importStatus.data.createdAt * 1000
		: processingStartedAt

	const pageState: PageState = useMemo(() => {
		if (overrideState?.kind === 'rate_limit') return 'rate_limit'
		if (overrideState?.kind === 'failed') return 'failed'

		const status = importStatus.data?.status
		if (!status) return 'idle'
		if (
			status === 'Failed' &&
			importStatus.data?.errorReason?.toLowerCase().includes('worker did not complete')
		) {
			return 'stuck'
		}
		if (status === 'Pending' || status === 'Processing') {
			if (processingStartedAtMs && Date.now() - processingStartedAtMs > STUCK_THRESHOLD_MS) {
				return 'stuck'
			}
			return 'processing'
		}
		if (status === 'Success') return 'success'
		if (status === 'NoEventsFound') return 'no_events'
		if (status === 'Failed') return 'failed'
		return 'idle'
	}, [importStatus.data?.errorReason, importStatus.data?.status, overrideState, processingStartedAtMs])

	const createdEventIds = useMemo(
		() => importStatus.data?.createdEventIds ?? [],
		[importStatus.data?.createdEventIds],
	)
	// Latch "loaded" once the imported events first appear in the events query, so
	// that deleting an imported event afterwards doesn't bounce us back to the
	// loading state (a deleted event is legitimately absent from the list).
	const [importedEventsLoaded, setImportedEventsLoaded] = useState(false)
	useEffect(() => {
		setImportedEventsLoaded(false)
	}, [importId])
	useEffect(() => {
		if (importedEventsLoaded || pageState !== 'success' || !eventsData) return
		const allPresent = createdEventIds.every(id => eventsData.events.some(event => event.id === id))
		if (createdEventIds.length === 0 || allPresent) {
			setImportedEventsLoaded(true)
		}
	}, [importedEventsLoaded, pageState, eventsData, createdEventIds])
	const isWaitingForImportedEvents =
		pageState === 'success' && createdEventIds.length > 0 && !importedEventsLoaded

	const handleFilesChosen = useCallback(
		async (incoming: File[]) => {
			if (!incoming.length) return
			setServerValidationMessage(undefined)
			const currentCount = files.length
			const newEntries: FileEntry[] = []
			for (let i = 0; i < incoming.length; i++) {
				const f = incoming[i]
				const pageCount = await countPdfPages(f)
				const error = validateFile(f, i, pageCount, currentCount)
				newEntries.push({ id: newFileId(), file: f, pageCount, error })
			}
			setFiles(prev => [...prev, ...newEntries])
		},
		[files.length],
	)

	const handleRemoveFile = (id: string) => {
		setServerValidationMessage(undefined)
		setFiles(prev => {
			const next = prev.filter(f => f.id !== id)
			return next.map((entry, idx) => {
				const error = validateFile(entry.file, idx, entry.pageCount, 0)
				return { ...entry, error }
			})
		})
	}

	const handleBrowseClick = () => fileInputRef.current?.click()

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const list = e.target.files ? Array.from(e.target.files) : []
		handleFilesChosen(list)
		if (e.target) e.target.value = ''
	}

	useEffect(() => {
		const onDragEnter = (e: DragEvent) => {
			if (e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')) {
				dragCounter.current += 1
				setIsDragOver(true)
				e.preventDefault()
			}
		}
		const onDragOver = (e: DragEvent) => {
			if (e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')) {
				e.preventDefault()
			}
		}
		const onDragLeave = () => {
			dragCounter.current = Math.max(0, dragCounter.current - 1)
			if (dragCounter.current === 0) setIsDragOver(false)
		}
		const onDrop = (e: DragEvent) => {
			e.preventDefault()
			dragCounter.current = 0
			setIsDragOver(false)
			const dropped = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : []
			if (dropped.length) handleFilesChosen(dropped)
		}
		window.addEventListener('dragenter', onDragEnter)
		window.addEventListener('dragover', onDragOver)
		window.addEventListener('dragleave', onDragLeave)
		window.addEventListener('drop', onDrop)
		return () => {
			window.removeEventListener('dragenter', onDragEnter)
			window.removeEventListener('dragover', onDragOver)
			window.removeEventListener('dragleave', onDragLeave)
			window.removeEventListener('drop', onDrop)
		}
	}, [handleFilesChosen])

	const applyServerValidationError = (response: ApiErrorResponse) => {
		const message = getServerValidationMessage(response)
		setServerValidationMessage(message)
		setFiles(prev => {
			if (prev.length === 0) return prev
			const matched = prev.map(entry =>
				message.includes(entry.file.name) ? { ...entry, error: message } : entry,
			)
			if (matched.some(entry => entry.error === message)) return matched
			return matched.map((entry, index) => (index === 0 ? { ...entry, error: message } : entry))
		})
	}

	const handleSubmit = async () => {
		if (submitDisabled || !tripId) return
		try {
			setServerValidationMessage(undefined)
			const result = await createImport.mutateAsync({
				tripId,
				text: notes.trim() || undefined,
				files: files.map(f => f.file),
			})
			setOverrideState(undefined)
			setProcessingStartedAt(Date.now())
			setImportId(result.importId)
		} catch (error) {
			const response = getApiErrorResponse(error)
			if (response) {
				if (isRateLimited(error)) {
					const ra = response.headers?.['retry-after']
					const data = response.data as { retryAfterSeconds?: number } | undefined
					const retryAfter =
						typeof ra === 'string' && /^\d+$/.test(ra)
							? Number(ra)
							: typeof data?.retryAfterSeconds === 'number'
								? data.retryAfterSeconds
								: 24 * 3600
					setOverrideState({ kind: 'rate_limit', retryAfter })
					return
				}
				if (isBadRequest(error) || isPayloadTooLarge(error)) {
					applyServerValidationError(response)
					return
				}
				if (isServerError(error)) {
					setOverrideState({ kind: 'failed' })
					return
				}
			}
			toast.error(getErrorMessage(error, 'Failed to start import.'))
		}
	}

	const handleRetryImport = async () => {
		if (!tripId || !importId || retryImport.isPending) return
		try {
			const result = await retryImport.mutateAsync({ tripId, importId })
			setOverrideState(undefined)
			setServerValidationMessage(undefined)
			setProcessingStartedAt(Date.now())
			setImportId(result.importId)
			setSearchParams({ job: result.importId }, { replace: true })
		} catch (error) {
			if (isRateLimited(error)) {
				const ra = getApiErrorResponse(error)?.headers?.['retry-after']
				setOverrideState({
					kind: 'rate_limit',
					retryAfter: typeof ra === 'string' && /^\d+$/.test(ra) ? Number(ra) : 24 * 3600,
				})
				return
			}
			toast.error(getErrorMessage(error, 'Failed to retry import.'))
		}
	}

	const handleBack = () => navigate(`/trips/${tripId}/itinerary`)

	const handleReset = () => {
		setFiles([])
		setNotes('')
		setServerValidationMessage(undefined)
		setImportId(undefined)
		setOverrideState(undefined)
		setProcessingStartedAt(null)
		setSearchParams({}, { replace: true })
	}

	const handleViewTimeline = () => navigate(`/trips/${tripId}/itinerary`)

	return {
		tripId,
		trip,
		tripLoading,
		pageState,
		files,
		notes,
		setNotes,
		isDragOver,
		serverValidationMessage,
		hasValidationErrors,
		validationErrorCount,
		submitDisabled,
		isSubmitting: createImport.isPending,
		processingStartedAtMs,
		isWaitingForImportedEvents,
		createdEventIds,
		events: eventsData?.events ?? [],
		importId,
		errorReason: importStatus.data?.errorReason ?? undefined,
		rateLimitSeconds: overrideState?.kind === 'rate_limit' ? overrideState.retryAfter : undefined,
		fileInputRef,
		onBack: handleBack,
		onSubmit: handleSubmit,
		onReset: handleReset,
		onViewTimeline: handleViewTimeline,
		onRetry: handleRetryImport,
		onBrowse: handleBrowseClick,
		onInputChange: handleInputChange,
		onRemoveFile: handleRemoveFile,
	}
}
